import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import type { CustomSectionDto } from '@/src/api/types/generated-types'
import { type FilterableByModule, LocationModule } from '@/src/hooks/location-module/location-module.types'
import { checkIsModuleEnabled, filterPathsEnabledByModule } from '@/src/hooks/location-module/location-module.utils'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { useQuery } from '@tanstack/react-query'

export const useLocationModules = () => {
  const session = useSessionStore()
  const userId = session.user?.pgId
  const companyId = session.user?.company.id
  const query = useQuery({
    queryKey: queryKeys.modules.toKeyWithArgs(userId),
    enabled: !!userId,
    queryFn: async (): Promise<{ enabledModules: LocationModule[]; customSection: CustomSectionDto | null }> => {
      if (!userId || !companyId) return { enabledModules: [], customSection: null }
      const { enabledModules, customSection } = await API.location.getEnabledModules({ userId, companyId })
      return { enabledModules: enabledModules as LocationModule[], customSection: customSection ?? null }
    },
    // Needed because the 1st request made by the OpenAPI will get 401, and if the retryDelay is left to default, the users will feel it.
    retryDelay: attemptIndex => Math.min(100 * 2 ** attemptIndex, 30000),
  })
  return query
}

const useIsLocationModuleEnabled = (module: LocationModule) => {
  const { data: enabledModulesData } = useLocationModules()
  return checkIsModuleEnabled(module, enabledModulesData?.enabledModules ?? [])
}

export const useIsProcurementModuleEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.PROCUREMENT)
}

export const useIsIso50001ModuleEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.ISO50001)
}

export const useIsMeasuresModuleEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.MEASURES)
}

export const useIsMessstellenModuleEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.MESSSTELLEN)
}

export const useIsAiInvoiceCheckingModuleEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.AI_INVOICE_CHECKING)
}

export const useIsCockpitEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.PROCUREMENT_FREEMIUM)
}

export const useIsReportsEnabled = () => {
  return useIsLocationModuleEnabled(LocationModule.REPORTS)
}

export const useFilteredByModulePaths = <T extends FilterableByModule>(paths: T[]) => {
  const { data: enabledModulesData } = useLocationModules()

  return filterPathsEnabledByModule(paths, enabledModulesData?.enabledModules ?? [], enabledModulesData?.customSection ?? null)
}
